import { useState, useMemo, useCallback, useEffect } from "react"
import ConnectorDialog from "../../ConnectorDialog"
import { CSVUploadForm } from "~/ui-rtk/components/features/ingestion"
import useConnectors from "~/ui-rtk/hooks/connectors"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { connectorIcons } from "~/ui-rtk/shared/constants/image-paths.contants"
import { copyTextToClipboard } from "~/ui-rtk/utils/clipboard"
import type { CustomConnectorDialogFormProps } from "../types"

type IngestionFormType = Omit<CustomConnectorDialogFormProps, "uri">

type DeliveryTab = "upload" | "email"

interface ReportConfig {
  displayName?: string
  info?: string
  validationSchema?: {
    requiredColumns?: string[]
    optionalColumns?: string[]
  }
}

interface IngestionConfig {
  deliveryMethods?: string[]
  reports?: Record<string, ReportConfig>
  emailConfig?: {
    jobTypePattern?: string
  }
}

const IngestionForm: React.FC<IngestionFormType> = ({
  isOpen,
  service,
  companyConnectorId,
  onClose,
}) => {
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [activeTab, setActiveTab] = useState<DeliveryTab>("upload")
  const [copied, setCopied] = useState(false)
  const { getConnectorById } = useConnectors()
  const company = useCurrentCompany()

  const connector = useMemo(
    () => getConnectorById(service),
    [getConnectorById, service],
  )

  const ingestionConfig = connector?.metadata?.ingestionConfig as
    | IngestionConfig
    | undefined
  const supportsEmail =
    ingestionConfig?.deliveryMethods?.includes("hybrid") &&
    ingestionConfig?.emailConfig?.jobTypePattern

  // Report types are nested under ingestionConfig.reports (APP-344). The user
  // picks one before uploading or copying the email address — every report
  // brings its own column requirements and recipient.
  const reportEntries = useMemo(
    () => Object.entries(ingestionConfig?.reports ?? {}),
    [ingestionConfig?.reports],
  )

  const [selectedReportType, setSelectedReportType] = useState<string>("")

  // Default to the first report when the dialog opens or the connector changes.
  useEffect(() => {
    if (reportEntries.length === 0) {
      setSelectedReportType("")
      return
    }
    if (!reportEntries.some(([key]) => key === selectedReportType)) {
      setSelectedReportType(reportEntries[0][0])
    }
  }, [reportEntries, selectedReportType])

  const selectedReport = ingestionConfig?.reports?.[selectedReportType]

  const ingestEmail = useMemo(() => {
    if (!supportsEmail || !company?.id || !selectedReportType) return null
    const pattern = ingestionConfig?.emailConfig?.jobTypePattern as
      | string
      | undefined
    if (!pattern) return null
    return pattern
      .replace("{report_type}", selectedReportType)
      .replace("{company_id}", company.id)
  }, [supportsEmail, ingestionConfig, company?.id, selectedReportType])

  const requiredColumns: string[] =
    selectedReport?.validationSchema?.requiredColumns ?? []
  const optionalColumns: string[] =
    selectedReport?.validationSchema?.optionalColumns ?? []

  const handleUploadSuccess = () => {
    setUploadSuccess(true)
    setTimeout(() => {
      onClose()
      setUploadSuccess(false)
    }, 2000)
  }

  const handleCopy = useCallback(() => {
    if (!ingestEmail) return
    void copyTextToClipboard(ingestEmail).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [ingestEmail])

  const connectorIcon =
    connector?.icon ||
    connectorIcons[service as keyof typeof connectorIcons] ||
    connectorIcons.archive

  const connectorName = connector?.name || service

  return (
    <ConnectorDialog
      imgSrc={connectorIcon}
      alt={`${connectorName} Logo`}
      title={`${connectorName} - Data Ingestion`}
      isOpen={isOpen}
      onClose={onClose}
    >
      {uploadSuccess ? (
        <div className="p-4 bg-system-success-500/10 border border-system-success-500/30 rounded-lg">
          <p className="text-system-success-400 text-center">
            CSV uploaded successfully! Processing will begin shortly.
          </p>
        </div>
      ) : (
        <>
          {reportEntries.length > 1 && (
            <div className="mb-4">
              <label className="block text-xs text-platinum/70 font-medium mb-1.5">
                Report type
              </label>
              <select
                className="select select-bordered w-full bg-system-blue-800 text-white"
                value={selectedReportType}
                onChange={e => setSelectedReportType(e.target.value)}
              >
                {reportEntries.map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.displayName || key}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedReport?.info && (
            <div className="mb-4 p-3 bg-basic-blue/10 border border-basic-blue/30 rounded-lg">
              <p className="text-xs text-platinum/70 font-medium mb-1">
                About this report
              </p>
              <p className="text-sm text-platinum whitespace-pre-line leading-5">
                {selectedReport.info}
              </p>
            </div>
          )}

          {supportsEmail && (
            <div className="flex gap-2 mb-4">
              <button
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "upload"
                    ? "bg-basic-blue text-white"
                    : "bg-system-blue-700 text-platinum hover:bg-basic-blue/50"
                }`}
                onClick={() => setActiveTab("upload")}
              >
                Upload CSV
              </button>
              <button
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "email"
                    ? "bg-basic-blue text-white"
                    : "bg-system-blue-700 text-platinum hover:bg-basic-blue/50"
                }`}
                onClick={() => setActiveTab("email")}
              >
                Send via Email
              </button>
            </div>
          )}

          {activeTab === "upload" && (
            <>
              <p className="leading-5 text-left text-base text-platinum mb-4">
                Select a CSV file to upload. It will be checked for the correct
                format and processed automatically.
              </p>
              {companyConnectorId && selectedReportType && (
                <CSVUploadForm
                  companyConnectorId={companyConnectorId}
                  reportType={selectedReportType}
                  onSuccess={handleUploadSuccess}
                  onError={undefined}
                />
              )}
            </>
          )}

          {activeTab === "email" && ingestEmail && (
            <div className="space-y-4">
              <p className="leading-5 text-left text-base text-platinum">
                Send your CSV or Excel (.xlsx) file as an email attachment to
                the address below. It will be checked and processed
                automatically.
              </p>

              <div className="flex items-center gap-2 p-3 bg-system-blue-800 rounded-lg border border-basic-blue/30">
                <span className="text-sm text-white font-mono break-all flex-1">
                  {ingestEmail}
                </span>
                <button
                  onClick={handleCopy}
                  className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-md bg-basic-blue text-white hover:bg-basic-blue/80 transition-colors"
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
          )}

          {/* File requirements — always visible, scoped to the selected report */}
          <div className="mt-4 p-3 bg-system-blue-800/50 rounded-lg border border-basic-blue/20 space-y-2">
            {requiredColumns.length > 0 && (
              <>
                <p className="text-xs text-platinum/70 font-medium">
                  Your CSV file must include these columns:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {requiredColumns.map(col => (
                    <span
                      key={col}
                      className="px-2 py-0.5 text-xs bg-basic-blue/30 text-platinum rounded"
                    >
                      {col}
                    </span>
                  ))}
                </div>
              </>
            )}
            {optionalColumns.length > 0 && (
              <>
                <p className="text-xs text-platinum/70 font-medium mt-2">
                  Optional columns:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {optionalColumns.map(col => (
                    <span
                      key={col}
                      className="px-2 py-0.5 text-xs bg-basic-blue/20 text-platinum/60 rounded"
                    >
                      {col}
                    </span>
                  ))}
                </div>
              </>
            )}
            <p className="text-xs text-platinum/50">
              {activeTab === "email"
                ? "Accepted formats: CSV (max 50 MB) or Excel .xlsx (max 25 MB)."
                : "Accepted format: CSV. Maximum file size: 50 MB."}
            </p>
          </div>
        </>
      )}
    </ConnectorDialog>
  )
}

export default IngestionForm
